// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyOrganization } from "@/types";
import { formatNumber, formatStorage, pressToOpen, readLangValue, rightClickToOpen, storageTone, usedPercent } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import Meter from "@/UI/atoms/Meter";
import StatusChip from "@/UI/atoms/StatusChip";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    organization: TyOrganization;
    onOpen: () => void;
    /**
     * Absent where the card is a **preview** of a list that owns the actions — the home screen shows
     * the newest companies and hands off to their own section rather than growing a second copy of
     * its menu. In that section it is always passed.
     */
    onActions?: (anchor: DOMRect) => void;
};

/**
 * One developer company. The card answers the three questions an admin has about one — is it paying,
 * is it running out of room, and how much is inside it — and stops there.
 *
 * The whole card opens it. Hunting for the one word that was a link is a tax on every row of every
 * list, and the more-actions button is the single thing on it that means something else.
 */
export default function OrganizationCard({ organization, onOpen, onActions }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const name = readLangValue(organization.name, lang) || organization.slug;
    const { storage, counts, subscription } = organization;
    const percent = usedPercent(storage.usedMb, storage.limitMb);

    /**
     * Blocked outranks everything: a blocked company is unreachable whatever it paid. Otherwise the
     * access state, and a grace row says so on top of it — `full` covers both, and "active" alone
     * would hide the fact that this one lapses on Thursday.
     */
    const accessTone = organization.isBlocked
        ? "bad"
        : organization.access === "full"
          ? "good"
          : organization.access === "readOnly"
            ? "bad"
            : "neutral";
    const accessLabel = organization.isBlocked ? t.organizations.access.blocked : t.organizations.access[organization.access];

    return (
        <article
            className="organization-card"
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={pressToOpen(onOpen)}
            onContextMenu={onActions ? rightClickToOpen(onActions) : undefined}
        >
            <div className="organization-card__row">
                <Avatar name={name} picture={organization.logo} size={46} rounded bordered />

                <div className="organization-card__identity">
                    <span className="organization-card__name">{name}</span>
                    {/* The company's public address, so it reads as one: latin, left to right. */}
                    <span className="organization-card__slug">{organization.slug}</span>
                </div>

                {onActions && (
                    <button
                        type="button"
                        className="organization-card__more"
                        onClick={(event) => {
                            event.stopPropagation();
                            onActions(event.currentTarget.getBoundingClientRect());
                        }}
                        aria-label={t.common.more}
                    >
                        <Icon name="more" size={18} />
                    </button>
                )}
            </div>

            <div className="organization-card__chips">
                <StatusChip size="sm" tone={accessTone} label={accessLabel} />
                {subscription?.status === "grace" && <StatusChip size="sm" tone="warn" label={t.organizations.access.grace} />}
                {subscription && <StatusChip size="sm" tone="dark" label={t.subscriptions.periods[subscription.period]} />}
                {organization.city && <StatusChip size="sm" tone="neutral" label={organization.city} />}
            </div>

            <Meter
                label={t.organizations.storage}
                value={t.organizations.storageOf
                    .replace("{used}", formatStorage(storage.usedMb, lang))
                    .replace("{limit}", formatStorage(storage.limitMb, lang))}
                percent={percent}
                tone={storageTone(percent)}
            />

            {/* Three figures in equal columns, each labelled under its number — not a table of one row. */}
            <div className="organization-card__counts">
                <div className="organization-card__count">
                    <span className="organization-card__count-value">{formatNumber(counts.projects)}</span>
                    <span className="organization-card__count-label">{t.organizations.counts.projects}</span>
                </div>
                <div className="organization-card__count">
                    <span className="organization-card__count-value">{formatNumber(counts.units)}</span>
                    <span className="organization-card__count-label">{t.organizations.counts.units}</span>
                </div>
                <div className="organization-card__count">
                    <span className="organization-card__count-value">{formatNumber(counts.clients)}</span>
                    <span className="organization-card__count-label">{t.organizations.counts.clients}</span>
                </div>
            </div>
        </article>
    );
}
